/* eslint-disable no-underscore-dangle */
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  FormGroup,
  Input,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import CheckboxDropdown from './CheckboxDropdown';
import ItemStatusIcons from './ItemStatusIcons';

// Sort item status columns according to severity of user action needed
const getSortValue = lot => {
  if (lot.unpublished) {
    return 1;
  }

  if (lot.images === 0) {
    return 2;
  }

  if (lot.potentiallyBanned) {
    return 3;
  }

  if (lot.banned) {
    return 4;
  }

  if (lot.isRestricted) {
    return 5;
  }

  if (lot.deleted) {
    return 6;
  }

  return 7;
};

const Step6TableColumns = ({
  handleCheckbox,
  handleCheckboxAll,
  handleDropdownMessage,
  dropdownCheck,
  isBanned,
  isTimed,
  isPotentiallyBanned,
  isRestricted,
  isUnpublished,
  noImage,
  routeParams,
  selectedLots,
  toggleDeleteLotModal,
  twoHrsPassed,
}) => {
  const tableColumns = [
    {
      Header: () => (
        <CheckboxDropdown
          dropdownCheck={dropdownCheck}
          handleCheckboxAll={handleCheckboxAll}
          handleDropdownMessage={handleDropdownMessage}
          isBanned={isBanned}
          isPotentiallyBanned={isPotentiallyBanned}
          isRestricted={isRestricted}
          isUnpublished={isUnpublished}
          noImage={noImage}
          selectedLots={selectedLots}
        />
      ),
      headerClassName: 'checkbox-dropdown-header',
      id: 'selectIcon',
      resizable: false,
      width: 50,
      headerStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      sortable: false,
      accessor: d => ({
        deleted: d.deleted,
        lotNumber: d.lotNumber,
        lotExt: d.lotExt,
      }),
      Cell: ({ value: { deleted }, row, original }) => (
        <FormGroup className="mb-0">
          <Label check>
            <Input
              className={`m-0 position-relative ${deleted ? 'cursor-not-allowed' : ''}`}
              type="checkbox"
              checked={[...selectedLots].some(x => x === original.lotID)}
              disabled={deleted}
              aria-label="Select Checkbox"
              onChange={() => handleCheckbox(original?.lotID)}
            />
          </Label>
        </FormGroup>
      )
    }, {
      Header: 'Actions',
      headerClassName: 'd-flex justify-content-center align-items-center',
      id: 'actions',
      width: 90,
      resizable: false,
      sortable: false,
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      accessor: d => ({
        deleted: d.deleted,
        lotExt: d.lotExt,
        lotNumber: d.lotNumber,
        orderbid: d.orderbid,
      }),
      Cell: ({ value: { deleted, lotExt, lotNumber, orderbid }, original }) => (
        <Fragment>
          {deleted || twoHrsPassed ? (
            <button
              className="border-0 bg-transparent px-0"
              aria-label="Edit Item Modal"
              disabled={true}
              type="button"
            >
              <FontAwesomeIcon
                icon="edit"
                className="color-gray-bg cursor-not-allowed"
              />
            </button>
          ) : (
            <Link
              to={{
                pathname: `/session/${routeParams.type}/${routeParams.groupID}/${routeParams.catalogID}/lot/${(original.lotID)}`,
                state: { linkedFrom: 'step6' }
              }}
              aria-label="Edit"
            >
              <FontAwesomeIcon icon="edit" className="color-blue" />
            </Link>
          )}
          <button
            className="border-0 bg-transparent"
            aria-label="Delete Item Modal"
            disabled={deleted || twoHrsPassed}
            onClick={() => toggleDeleteLotModal(lotNumber, lotExt, original.invNumber)}
            type="button"
          >
            <FontAwesomeIcon icon="trash-alt" className={deleted || twoHrsPassed ? 'color-gray-bg  cursor-not-allowed' : 'color-blue'} />
          </button>
          {orderbid
            && (
            <Fragment>
              <FontAwesomeIcon icon="dollar-sign" id={`orderBidTooltip-${lotNumber}${lotExt}`} className={`${deleted ? 'color-gray-bg cursor-not-allowed' : 'color-blue cursor-pointer'} ml-2`} />
              <UncontrolledTooltip placement="right" target={`orderBidTooltip-${lotNumber}${lotExt}`}>
                An absentee bid has been placed. Once an absentee bid has been placed on an item you may not change the starting bid {isTimed ? 'or reserve' : ''} for that item.
              </UncontrolledTooltip>
            </Fragment>
            )}
        </Fragment>
      )
    }, {
      Header: 'Item #',
      headerClassName: 'd-flex align-items-center',
      id: 'lotNumber',
      width: 70,
      accessor: d => ({
        lotExt: d.lotExt,
        lotNumber: d.lotNumber
      }),
      Cell: ({ value: { lotExt, lotNumber } }) => <p className="float-right mr-1 my-2">{lotNumber}{lotExt}</p>
    }, {
      Header: 'Image #',
      headerClassName: 'd-flex align-items-center',
      id: 'imgNumber',
      width: 80,
      sortMethod: (a, b) => (a.images - b.images),
      getProps: (state, rowInfo) => ({
        // (bnikom) don't show border if lot is deleted
        className: rowInfo && !rowInfo.original.deleted && rowInfo.original.images <= 0 ? 'no-image-border' : '',
      }),
      accessor: d => ({
        images: d.images,
      }),
      Cell: ({ value: { images } }) => <p className="float-right mr-1 my-2">{images}</p>
    }, {
      Header: 'Title',
      headerClassName: 'd-flex align-items-center',
      id: 'title',
      className: 'wrap-table-cell',
      sortMethod: (a, b) => (
        (a.lotTitle.toLowerCase() > b.lotTitle.toLowerCase())
        - (a.lotTitle.toLowerCase() < b.lotTitle.toLowerCase())
      ),
      accessor: d => ({
        lotTitle: d.lotTitle
      }),
      Cell: ({ value: { lotTitle } }) => (
        <p className="my-2">{lotTitle.length > 75 ? `${lotTitle.substring(0, 75)}...` : lotTitle}</p>
      )
    }, {
      Header: 'Item Status',
      headerClassName: 'd-flex justify-content-center align-items-center',
      id: 'status',
      width: 110,
      resizable: false,
      className: 'checkbox-dropdown-header',
      sortMethod: (a, b) => (getSortValue(a) - getSortValue(b)),
      accessor: d => ({
        banned: d.banned,
        deleted: d.deleted,
        images: d.images,
        isRestricted: d.isRestricted,
        lotExt: d.lotExt,
        lotNumber: d.lotNumber,
        potentiallyBanned: d.potentiallyBanned,
        unpublished: d.unpublished,
        invNumber: d.invNumber,
        lotID: d.lotID,
      }),
      Cell: ({
        value: {
          banned,
          deleted,
          images,
          isRestricted,
          potentiallyBanned,
          unpublished,
          lotID,
        }
      }) => (
        <p className="mr-1 my-2" style={{ display: 'flex', justifyContent: 'space-around' }}>{deleted ? 'deleted' : ItemStatusIcons({
          banned,
          images,
          isRestricted,
          potentiallyBanned,
          unpublished,
          lotID,
        })}
        </p>
      )

    },
  ];

  return tableColumns;
};

export default Step6TableColumns;
